import type { Spec } from "../../src/domain/spec";

export type { Spec, Fact, Lane, Capture, Refusal, Situation } from "../../src/domain/spec";

export type DemoStatus = "draft" | "drafting" | "review" | "live" | "paused" | "expired" | "promoted";

/** Where a running draft has got to. Mirrors `DraftStage` in src/db/queries/demos.ts. */
export type DraftStage =
  | "discovering" | "reading" | "offsite" | "extracting" | "verifying" | "writing" | "saving";

/** What GET /v1/demos/:id/draft answers: the progress panel's whole diet. */
export interface DraftRun {
  status: DemoStatus;
  run_id: string | null;
  error: string | null;
  stage: DraftStage | null;
  started_at: number | null;
  /** How many pages the run means to read; null until `discover` has decided. */
  pages_total: number | null;
  pages: { url: string; title: string; status: number; fetched_at: number }[];
  /** Only with `?run=1`: the live Workflows instance status, or null. */
  run: { status?: string } | null;
}

export interface DemoSummary {
  id: string;
  slug: string;
  business_name: string;
  kind: string;
  website_url: string;
  notes: string;
  status: DemoStatus;
  /** Off by default; true only for a demo somebody minted a passcode for. */
  has_passcode: boolean;
  /** 1 = this link offers no outbound, whatever the published spec's situations say. */
  outbound_off: number;
  expires_at: number | null;
  draft_run_id: string | null;
  draft_error: string | null;
  draft_stage: DraftStage | null;
  draft_pages_total: number | null;
  draft_started_at: number | null;
  created_at: number;
  updated_at: number;
  pending_count: number;
  calls: number;
  /** Presses of the call button - an attempt, connected or not. Not page loads. */
  opens: number;
  last_call_at: number | null;
  /** People who opened the link: loads whose page ran, not previews, scanners or us. */
  visits: number;
  /** Distinct browsers among those visits. */
  visitors: number;
  last_visit_at: number | null;
}

export interface Demo extends DemoSummary {
  draft_spec: Spec | null;
  published_spec: Spec | null;
}

export interface CallRecord {
  id: string;
  call_id: string;
  started_at: number | null;
  ended_at: number | null;
  duration_ms: number | null;
  lane: string;
  /** "inbound" - they rang the demo. "outbound" - the assistant rang them. */
  direction: "inbound" | "outbound";
  /** What it rang about, as it stood at the time. Null on an inbound call. */
  situation: { id: string; label: string } | null;
  end_reason: string;
  lead_card: { text: string | null; captured: Record<string, unknown>; caller: Record<string, unknown>; notes: string[] } | null;
  transcript: { role?: string; text?: string; language?: string }[];
  /**
   * The call's audio, or null for one with none - a call from before recordings shipped, one
   * where nobody spoke, one whose upload never arrived. No key: a reader never addresses the
   * object, it asks the Worker for `/v1/demos/:id/calls/:call_id/recording?v=<sha256>`.
   */
  recording: { sha256: string; bytes: number; durationMs: number; recordedAt: number } | null;
}

/** One person opening the link. Place is Cloudflare's reading of the connection. */
export interface VisitRecord {
  id: string;
  at: number;
  /** Seconds the page was visible; null for a visit that has not reported leaving yet. */
  seconds: number | null;
  /** When the first call was placed from this visit, if one was. */
  called_at: number | null;
  country: string | null;
  region: string | null;
  city: string | null;
  timezone: string | null;
  /** Who owns the network - a company's name on an office line, a carrier on a phone. */
  org: string | null;
  referrer: string;
  device: string;
  /** This browser had opened the link before. */
  returning: boolean;
}

export interface VisitSummary {
  visits: number;
  visitors: number;
  /** Link previews by previewer: {"Slack": 2, "iMessage": 1}. */
  previews: Record<string, number>;
  /** Loads whose page never ran - an email link scanner, most often. */
  unconfirmed: number;
  /** Our own, from a browser marked by "Open (not counted)". */
  internal: number;
}

/** Where the demo page lives. One Pages project serves every demo at /call/<slug>; this is
 * its custom domain. Links already sent on demo-line.pages.dev keep working - both hosts
 * serve the same project. */
export const DEMO_ORIGIN = "https://demo.replystate.com";
export const demoLink = (slug: string) => `${DEMO_ORIGIN}/call/${slug}`;
/** The same link, marking the browser that opens it as ours: its visits stop counting. */
export const ownLink = (slug: string) => `${demoLink(slug)}?me=1`;
